import { useState } from "react";
import { Download, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * Client-side PDF export via html2canvas-pro + jsPDF.
 *
 * Why "-pro": the vanilla html2canvas panics on modern CSS colour
 * functions (oklch, color-mix) that Tailwind v4 and shadcn's default
 * theme emit — the fork handles them natively.
 *
 * Usage:
 *   <PdfExportButton targetRef={pageRef} filename="scorecard-2026" />
 *   <div ref={pageRef}>…dashboard content…</div>
 *
 * The button clones the referenced DOM subtree onto a canvas, splits it
 * across A4 pages preserving the aspect ratio, and downloads the file.
 * Runs entirely on the client (~1-3 s for a full dashboard), no backend
 * hit and no auth headers to worry about.
 *
 * `beforeCapture` (optional): async hook called right before html2canvas
 * runs. Use it to expand collapsed sections, hide sticky headers, etc.
 * `afterCapture` restores whatever the hook mutated.
 */
export default function PdfExportButton({
  targetRef,
  filename = "reporte",
  label = "Descargar PDF",
  title,
  subtitle,
  beforeCapture,
  afterCapture,
  testid = "pdf-export-btn",
}) {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (!targetRef?.current) {
      toast.error("No hay contenido para exportar");
      return;
    }
    setBusy(true);
    let cleanup = null;
    try {
      // Lazy-load heavy deps so the initial JS bundle isn't inflated for
      // users who never click the button. Dynamic import returns a
      // module object with `.default` for jspdf.
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas-pro"),
        import("jspdf"),
      ]);

      cleanup = beforeCapture ? await beforeCapture() : null;

      const node = targetRef.current;
      // Give any pending renders / animations a beat to settle before
      // rasterising — otherwise transitions can produce ghost frames.
      await new Promise((r) => setTimeout(r, 150));

      const canvas = await html2canvas(node, {
        scale: 2,               // 2x for retina-quality output
        useCORS: true,
        logging: false,
        backgroundColor: "#f8fafc",
        // Ignore the export button itself so it doesn't render into the
        // PDF. Marked by `data-pdf-hide` on the wrapping element.
        ignoreElements: (el) => el.hasAttribute?.("data-pdf-hide"),
      });

      // A4 landscape gives more room for the wide dashboard layouts.
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const contentWidth = pageWidth - margin * 2;
      const contentHeight = pageHeight - margin * 2 - 12; // reserve header

      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Header on every page
      const drawHeader = (pageNum, totalPages) => {
        pdf.setFillColor(15, 23, 42);
        pdf.rect(0, 0, pageWidth, 10, "F");
        pdf.setTextColor(244, 196, 0);
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.text(title || "SDM Business Intelligence", margin, 6.5);
        if (subtitle) {
          pdf.setTextColor(255, 255, 255);
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(7);
          pdf.text(subtitle, margin + 60, 6.5);
        }
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(7);
        pdf.text(
          `${new Date().toLocaleString("es-DO")} · Página ${pageNum}/${totalPages}`,
          pageWidth - margin, 6.5, { align: "right" },
        );
      };

      const imgData = canvas.toDataURL("image/jpeg", 0.85);

      if (imgHeight <= contentHeight) {
        drawHeader(1, 1);
        pdf.addImage(imgData, "JPEG", margin, 14, imgWidth, imgHeight);
      } else {
        // Slice the tall canvas into page-sized chunks and paint one per
        // PDF page. We paint the full image on each page but shift the
        // y-offset upward, then clip via a filled rect at the bottom.
        const totalPages = Math.ceil(imgHeight / contentHeight);
        for (let p = 0; p < totalPages; p++) {
          if (p > 0) pdf.addPage();
          drawHeader(p + 1, totalPages);
          const yOffset = 14 - p * contentHeight;
          pdf.addImage(imgData, "JPEG", margin, yOffset, imgWidth, imgHeight);
          // Paint white bands above/below the visible slice so the
          // overflow from the tall image doesn't bleed onto the header
          // or the next slice.
          pdf.setFillColor(255, 255, 255);
          if (p > 0) pdf.rect(0, 0, pageWidth, 12, "F");
          pdf.rect(0, 14 + contentHeight, pageWidth, pageHeight, "F");
          // Redraw header on top of the white band.
          drawHeader(p + 1, totalPages);
        }
      }

      const ts = new Date().toISOString().slice(0, 10);
      pdf.save(`${filename}-${ts}.pdf`);
      toast.success("PDF generado", { description: `${filename}-${ts}.pdf` });
    } catch (err) {
      toast.error("Error al generar el PDF", {
        description: err?.message || "Intenta cerrar modales o gráficas animadas y vuelve a probar",
      });
      // eslint-disable-next-line no-console
      console.error("PDF export failed:", err);
    } finally {
      if (cleanup && afterCapture) {
        try { await afterCapture(cleanup); } catch { /* noop */ }
      }
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      data-testid={testid}
      data-pdf-hide
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-extrabold transition-all disabled:opacity-60"
      style={{
        background: busy ? "#e2e8f0" : "#0f172a",
        color: busy ? "#64748b" : "#f4c400",
        border: "1px solid " + (busy ? "#cbd5e1" : "#0f172a"),
        cursor: busy ? "not-allowed" : "pointer",
        boxShadow: busy ? "none" : "0 2px 6px rgba(15, 23, 42, 0.15)",
      }}
      title="Genera un PDF de esta vista con los filtros aplicados"
    >
      {busy
        ? <><Loader2 size={13} className="animate-spin" /> Generando…</>
        : <><Download size={13} /> {label}</>}
    </button>
  );
}

/** Compact icon-only variant for tight headers. */
export function PdfExportIconButton(props) {
  return <PdfExportButton {...props} label="" />;
}
